import create, {StateCreator} from 'zustand'
import {devtools, persist, PersistOptions} from 'zustand/middleware'
import {
	ApiAmount,
	ApiEmbeddedFundingAccount,
	ApiEmbeddedPartnerPartyAccount,
	ApiEmbeddedPartnerPartyProfileResponse,
	BasicPartnerPartyLink,
	BasicPartyInfo,
	BasicPartnerInfo,
	PartnerPartyEnroll,
	ApiEmbeddedCryptoTransactionResponse,
	ApiEmbeddedPartnerTransactionCryptoCurrencyTransactTypeEnum,
	ApiAmountCurrencyEnum,
	FiatAmountCurrencyEnum,
	FiatAmount,
	ApiEmbeddedCryptoTransactionRequestTransactTypeEnum,
	CryptoOrFiatAmountCurrencyEnum,
} from '@bakkt/api'
import {setUserProperties} from 'utils/firebase/analytics'

import {Application as BakktComponentApplication} from '@bakkt/components'
import {loadOnStartup, embeddedPartnerApi, apiCall} from '../api'
import {AnalyticsFlags} from 'utils/firebase/analyticsProperties'
import {showOnboardingErrorDialog, showTransactionErrorDialog} from 'components/ErrorDialog'

export type Amount = ApiAmount | FiatAmount | {amount?: number; currency?: CurrencyEnum}
export type TransactTypeEnum =
	| ApiEmbeddedPartnerTransactionCryptoCurrencyTransactTypeEnum
	| ApiEmbeddedCryptoTransactionRequestTransactTypeEnum
export const TransactType = ApiEmbeddedPartnerTransactionCryptoCurrencyTransactTypeEnum
export type FundingAccount = ApiEmbeddedFundingAccount
export type PartnerPartyProfile = ApiEmbeddedPartnerPartyProfileResponse
export type PartnerPartyAccount = ApiEmbeddedPartnerPartyAccount
export type CurrencyEnum =
	| ApiAmountCurrencyEnum
	| ApiAmountCurrencyEnum
	| FiatAmountCurrencyEnum
	| CryptoOrFiatAmountCurrencyEnum
	| undefined
export const Currency = ApiAmountCurrencyEnum || ApiAmountCurrencyEnum || FiatAmountCurrencyEnum
export interface CryptoTransactionConfirm {
	price: Amount
	quantity: Amount
	transactType?: TransactTypeEnum
	unitPrice?: Amount
	transactionFee: Amount
	totalPrice: Amount
}
export type HistoricalPeriod = 'YEAR' | 'MONTH' | 'WEEK' | 'DAY' | 'HOUR'

type NULL = null | undefined
export type EmbeddedWebState = {
	partner: BasicPartnerInfo | NULL
	partnerPartyLink: BasicPartnerPartyLink | NULL
	party: BasicPartyInfo | NULL
	partyProfile: PartnerPartyProfile | NULL
	loadProfile: () => void
	loadOnStartup: () => void
	missingProfileFields: Partial<PartnerPartyEnroll> | NULL
	updateMissingProfileFields: (missingProfileFields: Partial<PartnerPartyEnroll>) => void
	logout: () => void
	fundingAccounts: FundingAccount[]
	setFundingAccounts: (fundingAccounts: FundingAccount[]) => void
	selectedFundingAccount: FundingAccount | null
	setSelectedFundingAccount: (selectedFundingAccount: FundingAccount) => void
	selectedFundingAccountId: string | null
	setSelectedFundingAccountId: (selectedFundingAccountId: string) => void
	cryptoAccounts: PartnerPartyAccount[]
	setCryptoAccounts: (cryptoAccounts: PartnerPartyAccount[]) => void
	isEnrolled: Boolean
	fiatCurrencyPreference: CurrencyEnum
	selectedCryptoAccount: PartnerPartyAccount | null
	setSelectedCryptoAccount: (selectedCryptoAccount: PartnerPartyAccount) => void
	selectedCryptoAccountId: string | null
	setSelectedCryptoAccountId: (selectedCryptoAccountId: string) => void
	cryptoTransaction: ApiEmbeddedCryptoTransactionResponse | null
	setCryptoTransaction: (cryptoTransaction: ApiEmbeddedCryptoTransactionResponse | null) => void
	cryptoTransactionConfirm: CryptoTransactionConfirm | null
	setCryptoTransactionConfirm: (cryptoTransactionConfirm: CryptoTransactionConfirm | null) => void
	resetToHome: () => void
	isSuspended: Boolean
	setIsSuspended: (isSuspended: boolean) => void
	loadDefaultFundingSource: () => void
	activePartnerTransactionId: string | null
	setActivePartnerTransactionId: (activePartnerTransactionId: string | null) => void
	timezone: string
}

type EmbeddedWebPersist = (
	config: StateCreator<EmbeddedWebState>,
	options: PersistOptions<EmbeddedWebState>,
) => StateCreator<EmbeddedWebState>

export const useStore = create<EmbeddedWebState>(
	devtools(
		(persist as EmbeddedWebPersist)(
			(set, get) => ({
				partner: null,
				partnerPartyLink: null,
				party: null,
				loadOnStartup: async () => {
					const partyInfo = await loadOnStartup()
					setUserProperties({
						[AnalyticsFlags.USERNAME]: partyInfo?.partnerPartyLink?.userName,
						[AnalyticsFlags.PARTY_PARTNER_LINK_ID]: partyInfo?.partnerPartyLink?.partnerPartyLink?.id,
						[AnalyticsFlags.PARTNER_ID]: partyInfo?.partner?.id,
						[AnalyticsFlags.PARTNER_NAME]: partyInfo?.partner?.name,
						[AnalyticsFlags.PARTY_ID]: partyInfo?.party?.id,
					})
					const TIME_ZONE = 'America/New_York'
					BakktComponentApplication.timezone = TIME_ZONE

					const isEnrolled = !!partyInfo?.partnerPartyLink?.partnerPartyLink
					if (isEnrolled) {
						get().loadDefaultFundingSource()
					}

					set({
						partner: partyInfo?.partner,
						partnerPartyLink: partyInfo?.partnerPartyLink,
						party: partyInfo?.party,
						isEnrolled,
						timezone: TIME_ZONE,
					})
				},
				timezone: '',
				partyProfile: null,
				isEnrolled: false, //TODO this is derived data, so make it function
				loadProfile: async () => {
					const {response, error}: any = await apiCall(embeddedPartnerApi.fetchPartnerPartyProfile)
					if (error && error.code !== 'PARTY_SUSPENDED') {
						showOnboardingErrorDialog(get().partner)
					}
					set({
						partyProfile: response,
					})
				},
				missingProfileFields: null,
				updateMissingProfileFields: (missingProfileFields: Partial<PartnerPartyEnroll>) => {
					set({
						missingProfileFields,
					})
				},
				logout: () =>
					set({
						partner: null,
						partnerPartyLink: null,
						selectedCryptoAccount: null,
						fundingAccounts: [],
						selectedFundingAccount: null,
						cryptoAccounts: [],
					}),
				fundingAccounts: [],
				setFundingAccounts: (fundingAccounts: FundingAccount[]) => set({fundingAccounts}),
				selectedFundingAccount: null,
				setSelectedFundingAccount: (selectedFundingAccount: FundingAccount) => set({selectedFundingAccount}),
				selectedFundingAccountId: null,
				setSelectedFundingAccountId: (selectedFundingAccountId: string) => set({selectedFundingAccountId}),
				cryptoAccounts: [],
				setCryptoAccounts: (cryptoAccounts: PartnerPartyAccount[]) => set({cryptoAccounts}),
				selectedCryptoAccount: null,
				setSelectedCryptoAccount: (selectedCryptoAccount: PartnerPartyAccount) => set({selectedCryptoAccount}),
				selectedCryptoAccountId: null,
				setSelectedCryptoAccountId: (selectedCryptoAccountId: string) => set({selectedCryptoAccountId}),
				fiatCurrencyPreference: ApiAmountCurrencyEnum.USD,
				cryptoTransaction: null,
				setCryptoTransaction: (cryptoTransaction: ApiEmbeddedCryptoTransactionResponse | null) => {
					set({
						cryptoTransaction,
					})
				},
				activePartnerTransactionId: null,
				setActivePartnerTransactionId: (activePartnerTransactionId: string | null) => {
					set({
						activePartnerTransactionId,
					})
				},
				cryptoTransactionConfirm: null,
				setCryptoTransactionConfirm: (cryptoTransactionConfirm: CryptoTransactionConfirm | null) => {
					set({
						cryptoTransactionConfirm,
					})
				},
				resetToHome: async () => {
					get().loadDefaultFundingSource()

					set({
						cryptoTransactionConfirm: null,
						cryptoTransaction: null,
					})
				},
				isSuspended: false,
				setIsSuspended: (isSuspended: boolean) => set({isSuspended}),
				loadDefaultFundingSource: async () => {
					const {selectedFundingAccountId, partner} = get()
					const {response, error} = await apiCall(() =>
						embeddedPartnerApi.fetchFundingAccounts(ApiAmountCurrencyEnum.USD),
					)
					if (error && error.code !== 'PARTY_SUSPENDED') {
						showTransactionErrorDialog(partner)
					}

					const selectedAccount = (response || []).find(
						account => account.externalAccountRef === selectedFundingAccountId,
					)

					set({
						fundingAccounts: response || [],
						selectedFundingAccount: selectedAccount || response?.[0] || null,
						selectedFundingAccountId: selectedAccount?.externalAccountRef || null,
					})
				},
			}),
			{
				name: 'bakkt-embedded-web-storage',
				getStorage: () => sessionStorage,
				partialize: state => ({
					selectedCryptoAccountId: state.selectedCryptoAccountId,
					selectedFundingAccountId: state.selectedFundingAccountId,
				}),
			},
		),
	),
)
